import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { CircularProgress, Grid, Card, CardContent, Typography, Accordion, AccordionSummary, AccordionDetails, IconButton, Box } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ViewHeader from 'components/rootComponents/Standards/ViewHeader/ViewHeader';

interface CDPListadoProps {
  filters: Record<string, any>;
}

interface Contrato {
  tipo_contrato: string;
  total: number;
}

interface ContratoHoras {
  tipo_contrato: string;
  total_horas_contrato: number;
  total_horas_autorizadas: number;
}

interface CargosPresupuestadosDocentes {
  total_presupuestados: number;
  total_docentes: number;
}

const CDPListado: React.FC<CDPListadoProps> = ({ filters }) => {
  console.log("📌 CDPListado renderizado correctamente!");

  // Estados para empleados
  const [empleadosTotal, setEmpleadosTotal] = useState<number | null>(null);
  const [empleadosMasculino, setEmpleadosMasculino] = useState<number | null>(null);
  const [empleadosFemenino, setEmpleadosFemenino] = useState<number | null>(null);
  const [empleadosTransgenero, setEmpleadosTransgenero] = useState<number | null>(null);
  const [loadingEmpleados, setLoadingEmpleados] = useState<boolean>(true);

  // Estados para contratos por tipo
  const [contratos, setContratos] = useState<Contrato[]>([]);
  const [loadingContratos, setLoadingContratos] = useState<boolean>(true);

  // Estados para ingresos y egresos
  const [ingresos, setIngresos] = useState<number | null>(null);
  const [egresos, setEgresos] = useState<number | null>(null);
  const [loadingIngresosEgresos, setLoadingIngresosEgresos] = useState<boolean>(true);

  // Estados para horas por contrato
  const [contratosHoras, setContratosHoras] = useState<ContratoHoras[]>([]);
  const [loadingContratosHoras, setLoadingContratosHoras] = useState<boolean>(true);

  // Estados para cargos presupuestados y docentes efectivos
  const [cargosPresupuestadosDocentes, setCargosPresupuestadosDocentes] = useState<CargosPresupuestadosDocentes | null>(null);
  const [loadingCargos, setLoadingCargos] = useState<boolean>(true);

  useEffect(() => {
    // Función para obtener datos de empleados por género
    const fetchEmpleados = async () => {
      try {
        const response = await fetch('https://ec2-18-231-158-5.sa-east-1.compute.amazonaws.com:4000/v1.1/cdp/empleados_genero', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(filters)
        });
        const data = await response.json();
        console.log("✅ Datos recibidos en Empleados:", data);
        setEmpleadosTotal(data?.data?.data?.total || 0);
        setEmpleadosMasculino(data?.data?.data?.masculino || 0);
        setEmpleadosFemenino(data?.data?.data?.femenino || 0);
        setEmpleadosTransgenero(data?.data?.data?.transgenero || 0);
      } catch (error) {
        console.error("🚨 Error al obtener empleados:", error);
        setEmpleadosTotal(null);
        setEmpleadosMasculino(null);
        setEmpleadosFemenino(null);
        setEmpleadosTransgenero(null);
      } finally {
        setLoadingEmpleados(false);
      }
    };

    // Función para obtener contratos por tipo
    const fetchContratos = async () => {
      try {
        const response = await fetch('https://ec2-18-231-158-5.sa-east-1.compute.amazonaws.com:4000/v1.1/cdp/contratos_por_tipo', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(filters)
        });
        const data = await response.json();
        console.log("✅ Datos recibidos en Contratos:", data);
        setContratos(data?.data || []);
      } catch (error) {
        console.error("🚨 Error al obtener contratos:", error);
        setContratos([]);
      } finally {
        setLoadingContratos(false);
      }
    };

    // Función para obtener ingresos y egresos
    const fetchIngresosEgresos = async () => {
      try {
        const response = await fetch('https://ec2-18-231-158-5.sa-east-1.compute.amazonaws.com:4000/v1.1/cdp/ingresos-egresos', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(filters)
        });
        const data = await response.json();
        console.log("✅ Datos recibidos en Ingresos y Egresos:", data);
        setIngresos(data?.ingresos ?? 0);
        setEgresos(data?.egresos ?? 0);
      } catch (error) {
        console.error("🚨 Error al obtener ingresos y egresos:", error);
        setIngresos(null);
        setEgresos(null);
      } finally {
        setLoadingIngresosEgresos(false);
      }
    };

    // Función para obtener la cantidad de horas por tipo de contrato
    const fetchContratosHoras = async () => {
      try {
        const response = await fetch('https://ec2-18-231-158-5.sa-east-1.compute.amazonaws.com:4000/v1.1/cdp/cantidad_horas', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(filters)
        });
        const data = await response.json();
        console.log("✅ Datos recibidos en Contratos Total Horas:", data);
        setContratosHoras(data?.data || []);
      } catch (error) {
        console.error("🚨 Error al obtener contratos total horas:", error);
        setContratosHoras([]);
      } finally {
        setLoadingContratosHoras(false);
      }
    };

    // Función para obtener cargos presupuestados y docentes efectivos
    const fetchCargosPresupuestadosDocentes = async () => {
      try {
        const response = await fetch('https://ec2-18-231-158-5.sa-east-1.compute.amazonaws.com:4000/v1.1/cdp/cargos_presupuestados_docentes_activos', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(filters)
        });
        const data = await response.json();
        console.log("✅ Datos recibidos en Cargos Presupuestados y Docentes:", data);
        setCargosPresupuestadosDocentes(data?.data || { total_presupuestados: 0, total_docentes: 0 });
      } catch (error) {
        console.error("🚨 Error al obtener cargos presupuestados y docentes:", error);
        setCargosPresupuestadosDocentes(null);
      } finally {
        setLoadingCargos(false);
      }
    };

    // Ejecutar todas las peticiones
    fetchEmpleados();
    fetchContratos();
    fetchIngresosEgresos();
    fetchContratosHoras();
    fetchCargosPresupuestadosDocentes();
  }, [filters]);

  // Estilo fijo para las cards
// Ajuste en Card para mantener el mismo tamaño
const cardStyle = {
  height: '180px',
  minWidth: '250px',
  display: 'flex',
  flexDirection: 'column' as const,
  justifyContent: 'center',
  alignItems: 'flex-start',
  padding: '16px',
  backgroundColor: '#F5F5F5',
};


  return (
    <Grid container spacing={2} sx={{ marginTop: '20px', paddingLeft: '20px' }}>
    <ViewHeader title="Indicadores CDP" />
    
    {/* Espacio en blanco para mayor separación */}
    <Box sx={{ height: '40px' }} />
  {/* Sección de Empleados */}
<Accordion sx={{ width: '100%' }}>
  <AccordionSummary
    expandIcon={<ExpandMoreIcon />}
    sx={{ display: 'flex', flexDirection: 'row-reverse', alignItems: 'center' }}
  >
    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>Empleados Activos</Typography>
  </AccordionSummary>
  <AccordionDetails>
    {/* Total de Empleados centrado en la línea, pero con texto alineado a la izquierda */}
    <Grid container justifyContent="center" spacing={3}>
      <Grid item xs={12} md={4}>
        <Card sx={cardStyle}>
          <CardContent sx={{ textAlign: 'left' }}>
            <Typography variant="h6">Total de Empleados</Typography>
            {loadingEmpleados ? (
              <CircularProgress />
            ) : (
              <Typography variant="h4">
                {empleadosTotal !== null ? empleadosTotal : 'Error'}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>

    {/* Cards para Género */}
    <Grid container spacing={3} sx={{ marginTop: 2 }}>
      <Grid item xs={12} md={4}>
        <Card sx={cardStyle}>
          <CardContent sx={{ textAlign: 'left' }}>
            <Typography variant="h6">Empleados Masculinos</Typography>
            {loadingEmpleados ? (
              <CircularProgress />
            ) : (
              <Typography variant="h4">
                {empleadosMasculino !== null ? empleadosMasculino : 'Error'}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={4}>
        <Card sx={cardStyle}>
          <CardContent sx={{ textAlign: 'left' }}>
            <Typography variant="h6">Empleados Femeninos</Typography>
            {loadingEmpleados ? (
              <CircularProgress />
            ) : (
              <Typography variant="h4">
                {empleadosFemenino !== null ? empleadosFemenino : 'Error'}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={4}>
        <Card sx={cardStyle}>
          <CardContent sx={{ textAlign: 'left' }}>
            <Typography variant="h6">Empleados Transgénero</Typography>
            {loadingEmpleados ? (
              <CircularProgress />
            ) : (
              <Typography variant="h4">
                {empleadosTransgenero !== null ? empleadosTransgenero : 'Error'}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  </AccordionDetails>
</Accordion>

      {/* Sección de Contratos por Tipo */}

<Accordion sx={{ width: '100%', minWidth: '100%' }}>

      <AccordionSummary
  expandIcon={<ExpandMoreIcon />}
  sx={{ 
    display: 'flex', 
    flexDirection: 'row-reverse', 
    alignItems: 'center' 
  }}
>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>Contratos por Tipo</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            {loadingContratos ? (
              <Grid item xs={12}>
                <CircularProgress />
              </Grid>
            ) : contratos.length > 0 ? (
              contratos.map((contrato, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Card sx={cardStyle}>
                    <CardContent sx={{ textAlign: 'left' }}>
                      <Typography variant="h6">{contrato.tipo_contrato}</Typography>
                      <Typography variant="h4">{contrato.total}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))
            ) : (
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ textAlign: 'left' }}>
                  No se encontraron contratos
                </Typography>
              </Grid>
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Sección de Ingresos y Egresos */}
<Accordion sx={{ width: '100%', minWidth: '100%' }}>

      <AccordionSummary
  expandIcon={<ExpandMoreIcon />}
  sx={{ 
    display: 'flex', 
    flexDirection: 'row-reverse', 
    alignItems: 'center' 
  }}
>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>Ingresos y Egresos</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card sx={cardStyle}>
                <CardContent sx={{ textAlign: 'left' }}>
                  <Typography variant="h6">Ingresos</Typography>
                  {loadingIngresosEgresos ? (
                    <CircularProgress />
                  ) : (
                    <Typography variant="h4">
                      {ingresos !== null ? ingresos : 'Error'}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={cardStyle}>
                <CardContent sx={{ textAlign: 'left' }}>
                  <Typography variant="h6">Egresos</Typography>
                  {loadingIngresosEgresos ? (
                    <CircularProgress />
                  ) : (
                    <Typography variant="h4">
                      {egresos !== null ? egresos : 'Error'}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Sección de Horas por Tipo de Contrato */}
<Accordion sx={{ width: '100%', minWidth: '100%' }}>

      <AccordionSummary
  expandIcon={<ExpandMoreIcon />}
  sx={{ 
    display: 'flex', 
    flexDirection: 'row-reverse', 
    alignItems: 'center' 
  }}
>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>Horas por Tipo de Contrato</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            {loadingContratosHoras ? (
              <Grid item xs={12}>
                <CircularProgress />
              </Grid>
            ) : contratosHoras.length > 0 ? (
              contratosHoras.map((item, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Card sx={cardStyle}>
                    <CardContent sx={{ textAlign: 'left' }}>
                      <Typography variant="h6">{item.tipo_contrato}</Typography>
                      <Typography variant="subtitle1">
                        Horas Contrato: {item.total_horas_contrato}
                      </Typography>
                      <Typography variant="subtitle1">
                        Horas Autorizadas: {item.total_horas_autorizadas}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))
            ) : (
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ textAlign: 'left' }}>
                  No se encontraron datos de horas
                </Typography>
              </Grid>
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Sección de Cargos Presupuestados y Docentes Efectivos */}
<Accordion sx={{ width: '100%', minWidth: '100%' }}>

      <AccordionSummary
  expandIcon={<ExpandMoreIcon />}
  sx={{ 
    display: 'flex', 
    flexDirection: 'row-reverse', 
    alignItems: 'center' 
  }}
>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>Cargos Presupuestados y Docentes Efectivos</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            {loadingCargos ? (
              <Grid item xs={12}>
                <CircularProgress />
              </Grid>
            ) : cargosPresupuestadosDocentes ? (
              <>
                <Grid item xs={12} md={6}>
                  <Card sx={cardStyle}>
                    <CardContent sx={{ textAlign: 'left' }}>
                      <Typography variant="h6">Cargos Presupuestados</Typography>
                      <Typography variant="h4">
                        {cargosPresupuestadosDocentes.total_presupuestados}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card sx={cardStyle}>
                    <CardContent sx={{ textAlign: 'left' }}>
                      <Typography variant="h6">Docentes Efectivos</Typography>
                      <Typography variant="h4">
                        {cargosPresupuestadosDocentes.total_docentes}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </>
            ) : (
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ textAlign: 'left' }}>
                  No se encontraron datos de cargos
                </Typography>
              </Grid>
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Grid>
  );
};

const mapStateToProps = (state: any) => ({
  filters: state.filters,
});

export default connect(mapStateToProps)(CDPListado);
